// packages/frontend/src/components/BulkActionsBar.tsx
import { useState } from 'react'
import { useFileStore } from '../stores/fileStore'
import { useToastStore } from '../stores/toastStore'
import { downloadUrl } from '../config'
import { 
  Trash2, 
  Download, 
  Move, 
  Eye, 
  X,
  AlertTriangle,
  Loader2
} from 'lucide-react'

interface BulkActionsBarProps {
  selectedItems: Set<string>
  items: any[]
  onClearSelection: () => void
  onMove: (items: any[]) => void
}

export function BulkActionsBar({ selectedItems, items, onClearSelection, onMove }: BulkActionsBarProps) {
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const { bulkDelete } = useFileStore() // Use bulkDelete instead of deleteItem

  if (selectedItems.size === 0) return null

  const selectedItemsData = items.filter(item => selectedItems.has(item.item.id))
  const fileCount = selectedItemsData.filter(item => item.type === 'file').length
  const folderCount = selectedItemsData.filter(item => item.type === 'folder').length

  const getSelectionSummary = () => {
    const parts = []
    if (fileCount > 0) parts.push(`${fileCount} file${fileCount !== 1 ? 's' : ''}`)
    if (folderCount > 0) parts.push(`${folderCount} folder${folderCount !== 1 ? 's' : ''}`)
    return parts.join(' and ')
  }

  const handleBulkDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true)
      return
    }

    setIsDeleting(true)

    try {
      // Prepare items for bulk delete API
      const itemsToDelete = selectedItemsData.map(item => ({
        id: item.item.id,
        type: item.type
      }))

      // Use the proper bulkDelete function from the store
      await bulkDelete(itemsToDelete)

      // Clear selection after successful deletion
      onClearSelection()
    } catch (error) {
      console.error('Bulk delete error:', error)
      useToastStore.getState().addToast({
        type: 'error',
        message: 'Bulk delete operation failed'
      })
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const handleBulkDownload = async () => {
    const files = selectedItemsData.filter(item => item.type === 'file')
    
    if (files.length === 0) {
      useToastStore.getState().addToast({
        type: 'warning',
        message: 'No files selected for download'
      })
      return
    }

    if (files.length === 1) {
      // Single file - direct download
      window.open(downloadUrl(files[0].item.path), '_blank')
    } else {
      // Multiple files - download with delays to avoid popup blocker
      useToastStore.getState().addToast({
        type: 'info',
        message: `Starting download of ${files.length} files...`,
        duration: 3000
      })

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const url = downloadUrl(file.item.path)

        // Use a small delay between downloads to avoid popup blocker
        setTimeout(() => {
          const link = document.createElement('a')
          link.href = url
          link.download = file.item.name
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          // Show progress
          if (i === files.length - 1) {
            useToastStore.getState().addToast({
              type: 'success',
              message: `All ${files.length} files downloaded successfully`
            })
          }
        }, i * 500) // 500ms delay between each download
      }
    }
  }

  const handleToggleVisibility = async () => {
    // For now, we'll show this as coming soon
    useToastStore.getState().addToast({
      type: 'info',
      message: 'Bulk visibility change coming soon!'
    })
  }

  const getDeleteButtonText = () => {
    if (isDeleting) return 'Deleting...'
    if (showDeleteConfirm) {
      if (folderCount > 0) {
        return folderCount === 1 ? 'Delete Folder + Contents' : 'Delete Folders + Contents'
      }
      return 'Confirm Delete'
    }
    return 'Delete'
  }

  const getDeleteButtonIcon = () => {
    if (isDeleting) return <Loader2 className="h-4 w-4 animate-spin" />
    if (showDeleteConfirm && folderCount > 0) return <AlertTriangle className="h-4 w-4" />
    return <Trash2 className="h-4 w-4" />
  }

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
      <div className="flex items-center gap-3 rounded-xl border border-dark-600 bg-dark-800/95 p-4 shadow-xl backdrop-blur-sm">
        {/* Selection Info */}
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500/20 text-sm font-medium text-primary-400">
            {selectedItems.size}
          </div>
          <span className="text-sm font-medium text-white">
            {getSelectionSummary()} selected
          </span>
        </div>

        <div className="h-6 w-px bg-dark-600" />

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Download */}
          <button
            onClick={handleBulkDownload}
            disabled={fileCount === 0}
            className="flex items-center gap-2 rounded-lg bg-blue-500/20 px-3 py-2 text-sm font-medium text-blue-400 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Download selected files"
          >
            <Download className="h-4 w-4" />
            Download
          </button>

          {/* Toggle Visibility */}
          <button
            onClick={handleToggleVisibility}
            className="flex items-center gap-2 rounded-lg bg-yellow-500/20 px-3 py-2 text-sm font-medium text-yellow-400 hover:bg-yellow-500/30"
            title="Toggle visibility"
          >
            <Eye className="h-4 w-4" />
            Visibility
          </button>

          {/* Move */}
          <button
            onClick={() => onMove(selectedItemsData)}
            className="flex items-center gap-2 rounded-lg bg-purple-500/20 px-3 py-2 text-sm font-medium text-purple-400 hover:bg-purple-500/30"
            title="Move selected items"
          >
            <Move className="h-4 w-4" />
            Move
          </button>

          {/* Delete */}
          <button
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              showDeleteConfirm
                ? folderCount > 0
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
            } disabled:opacity-50`}
            title={
              showDeleteConfirm 
                ? folderCount > 0 
                  ? 'Will delete folders and all their contents - click to confirm'
                  : 'Click again to confirm' 
                : 'Delete selected items'
            }
          >
            {getDeleteButtonIcon()}
            {getDeleteButtonText()}
          </button>
        </div>

        <div className="h-6 w-px bg-dark-600" />

        {/* Clear Selection */}
        <button
          onClick={onClearSelection}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-dark-400 hover:bg-dark-700 hover:text-white"
          title="Clear selection"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Confirmation overlay */}
      {showDeleteConfirm && folderCount > 0 && (
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span>
              {folderCount === 1 
                ? 'This will delete the folder and ALL its contents!' 
                : `This will delete ${folderCount} folders and ALL their contents!`
              }
            </span>
          </div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-red-600"></div>
        </div>
      )}
    </div>
  )
}